/**
 * CoinShare (PULSE-220) — public live summary for one coin: full Coinglass
 * chart set + liquidation heatmap. Linked from Telegram signal messages.
 * Public by default (token = capability); SHARE_REQUIRE_AUTH can close it.
 */
import { useState, type ReactNode } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'

import { HeatmapPanel } from '@/components/market/HeatmapPanel'
import { MarketChart, type TradeMarkers } from '@/components/market/MarketChart'
import { useCoinSummary } from '@/hooks/useDashboardApi'

// PULSE-381: палитра TradingView/CoinGlass dark — визуал 1-в-1
const INK = '#0f121a'
const CARD = '#131722'
const LINE = '#2a2e39'
const DIM = '#787b86'
const TXT = '#d1d4dc'
const UP = '#089981'
const DOWN = '#f23645'
const TFS = [['1m', '1m'], ['3m', '3m'], ['5m', '5m'], ['15m', '15m'], ['30m', '30m'], ['1h', '1H'], ['4h', '4H']] as const

function Mark() {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
      <svg width="20" height="20" viewBox="0 0 32 32" aria-hidden="true">
        <rect width="32" height="32" rx="7" fill="#15161a" />
        <path d="M16 6 L26 24 L16 19 L6 24 Z" fill="#FF4524" />
      </svg>
      <span style={{ color: '#f4ede1', fontWeight: 500, letterSpacing: '.06em', fontSize: 14 }}>PULSAR</span>
    </span>
  )
}

export function CoinShare() {
  const { token } = useParams<{ token: string }>()
  const [params] = useSearchParams()
  const at = Number(params.get('at')) || undefined          // PULSE-414: график на момент at (unix)
  const [tf, setTf] = useState<string>(params.get('tf') ?? '5m')
  const { data, isLoading, isError, isFetching } = useCoinSummary(token, tf, at)
  // PULSE-418: отметки сделки из URL (?e=<unix>&ep=<цена>&x=<unix>&xp=<цена>&side=short) — разбор сделок Ильи
  const num = (k: string) => Number(params.get(k)) || undefined
  const markers: TradeMarkers | undefined = num('e') ? {
    direction: params.get('side') ?? 'short',
    entry: { t: num('e') as number, price: num('ep') ?? 0 },
    exit: num('x') ? { t: num('x') as number, price: num('xp') ?? 0 } : null,
  } : undefined

  const wrap = (inner: ReactNode) => (
    <div style={{ minHeight: '100vh', background: INK, color: TXT, fontFamily: 'Inter, system-ui, sans-serif',
      display: 'flex', justifyContent: 'center', padding: 16, boxSizing: 'border-box' }}>
      <div style={{ width: '100%', maxWidth: 1600 }}>{inner}</div>
    </div>
  )

  if (isLoading) return wrap(<div style={{ color: DIM, padding: '40px 0', textAlign: 'center' }}>Загрузка сводки…</div>)
  if (isError || !data) return wrap(<div style={{ color: DOWN, padding: '40px 0', textAlign: 'center' }}>Монета не найдена или ссылка устарела.</div>)

  const coin = data.symbol.replace('USDT', '')
  const candles = data.series.candles
  const last = candles.length ? candles[candles.length - 1] : null
  const chg = last && candles.length > 1
    ? ((last.c - candles[0].o) / candles[0].o) * 100 : null

  return wrap(
    <div style={{ background: CARD, border: `0.5px solid ${LINE}`, borderRadius: 14, overflow: 'hidden' }}>
      {/* header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', padding: '11px 16px', borderBottom: `0.5px solid ${LINE}` }}>
        <Mark />
        <span style={{ width: 1, height: 16, background: LINE }} />
        <span style={{ fontSize: 16, fontWeight: 500 }}>{coin}<span style={{ color: DIM, fontSize: 12 }}>USDT</span></span>
        {last && <span style={{ fontSize: 15, fontFamily: 'JetBrains Mono, monospace' }}>{last.c}</span>}
        {chg != null && (
          <span style={{ color: chg >= 0 ? UP : DOWN, fontSize: 13, fontFamily: 'JetBrains Mono, monospace' }}>
            {chg >= 0 ? '+' : ''}{chg.toFixed(2)}%
          </span>
        )}
        <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 2 }}>
          {TFS.map(([v, label]) => (
            <button key={v} onClick={() => setTf(v)} style={{
              background: 'transparent', border: 0, cursor: 'pointer', padding: '3px 7px', borderRadius: 4,
              fontSize: 13, fontWeight: 600, color: tf === v ? '#2962ff' : TXT,
            }}>{label}</button>
          ))}
          <span style={{ width: 7, height: 7, borderRadius: '50%', marginLeft: 8, background: isFetching ? DIM : UP }} />
        </span>
      </div>

      {/* full Coinglass chart set */}
      <div style={{ height: 'max(860px, calc(100vh - 140px))', padding: '0 0 2px' }}>
        <MarketChart data={data.series} markers={markers} />
      </div>

      {/* liquidation heatmap */}
      <div style={{ borderTop: `0.5px solid ${LINE}`, padding: '8px 10px 4px' }}>
        <div style={{ color: DIM, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', padding: '4px 6px' }}>
          Карта ликвидаций
        </div>
        <div style={{ height: 360 }}>
          <HeatmapPanel data={data.heatmap} candles={candles} />
        </div>
      </div>

      {/* footer */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '12px 16px', borderTop: `0.5px solid ${LINE}`, color: DIM, fontSize: 12 }}>
        <Mark />
        <span style={{ color: DIM }}>by Paradigma</span>
        <a href="https://pulsar.paradigmadev.com" style={{ marginLeft: 'auto', color: DIM, textDecoration: 'none' }}>pulsar.paradigmadev.com</a>
      </div>
    </div>,
  )
}
